<template>
  <div class="wrapper">
    <p class="wrapper_title">Буффер</p>
    <div class="header">
      <Filters
        :filters="this.filters"
        :onChange="this.onFiltersChange"
        :onDateChange="this.onDateChange"
        :margin="true"
      >
        <template #default="{ filters, onChange, onDateChange }">
          <div class="filters__item" style="width: 120px;">
            <CustomSelect
              :options="this.floors"
              label="Этаж"
              optionValueKey="value"
              optionLabelKey="label"
              :value="filters.level_num"
              @change="(value) => onChange('level_num', value)"
            />
          </div>

          <div class="filters__item" style="width: 140px;">
            <div class="filters__item-label">Дата начала</div>
            <CustomDate
              placeholder="дд.мм.гггг"
              :full-width="true"
              :value="filters.rowversion_from || ''"
              @change_date="(value) => onDateChange('rowversion_from', value)"
            />
          </div>
          <div class="filters__item" style="width: 140px;">
            <div class="filters__item-label">Дата конца</div>
            <CustomDate
              placeholder="дд.мм.гггг"
              :full-width="true"
              :value="filters.rowversion_to || ''"
              @change_date="(value) => onDateChange('rowversion_to', value)"
            />
          </div>
        </template>
      </Filters>
    </div>
    <div class="content-layout">
      <div class="table-section">
        <div
          v-if="!isDataReadyToShow || isDateSelectionIncomplete"
          class="placeholder-message"
        >
          <p class="placeholder-text">
            {{ getPlaceholderMessage() }}
          </p>
        </div>

        <CustomTable
          v-else
          :rowsData="this.data"
          :columns="this.bufferParamColumns"
          :loading="isFetching"
          :custom-body="true"
          :sticky-header="true"
          :page-header-height="150"
          :hide-top-title="true"
          :checkbox="true"
          :checkbox-value="allSelected"
          @all-check-click="onAllCheckClick"
        >
          <template #customBody="props">
            <q-tr>
              <custom-table-cell align="center" vertical-align="center">
                <CustomCheckbox
                  :value="selectedRows.includes(props.row.floor_buffer_id)"
                  @change="(value) => onRowCheckClick(props.row.floor_buffer_id, value)"
                />
              </custom-table-cell>
              <custom-table-cell align="center" vertical-align="center">
                {{ props.row.index }}
              </custom-table-cell>
              <custom-table-cell align="left" vertical-align="center">
                <span>{{ props.row.floor_param_type_name }}</span>
              </custom-table-cell>
              <custom-table-cell align="left" vertical-align="center">
                <span>{{ props.row.room_name }}</span>
              </custom-table-cell>
              <custom-table-cell align="left" vertical-align="center">
                <span>{{ props.row.floor_buffer_id }}</span>
              </custom-table-cell>
              <custom-table-cell align="left" vertical-align="center">
                <span>{{ props.row.floor_param_id }}</span>
              </custom-table-cell>
              <custom-table-cell
                align="left"
                vertical-align="center"
                style="font-size: 8px !important;"
              >
                {{ props.row.param_geom }}
              </custom-table-cell>
              <custom-table-cell align="center" vertical-align="center">
                {{ props.row.rowversion }}
              </custom-table-cell>
              <custom-table-cell align="left" vertical-align="center">
                {{ props.row.code }}
              </custom-table-cell>
            </q-tr>
          </template>
        </CustomTable>
      </div>
      <BufferSchemaView
        :bufferParams="selectedBufferParams"
        @refresh-data="refreshData"
        :savedFilters="this.savedFilters"
        :rowsData="this.rowsData"
        @update-rows="applyNewRows"
        :selectedBufferParamsType="selectedBufferParamsType"
      />
    </div>
  </div>
</template>

<script>
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import CustomTable from "@/components/common/CustomTable";
import CustomTableCell from "@/components/common/CustomTable/custom-table-cell";
import CustomCheckbox from "@/components/form/CustomCheckbox";
import CustomDate from "@/components/form/CustomDate";
import CustomSelect from "@/components/form/CustomSelect";
import CustomInput from "@/components/form/CustomInput";
import {
  bufferParamColumns,
  getBufferParamsList,
  getFloors
} from "@/views/BufferParams/services";
import Filters from "../BuildingSettings/filters.vue";
import BufferSchemaView from "./BufferSchemaView.vue";

export default {
  name: "BufferParams",
  components: { CustomInput, CustomTableCell, CustomTable, CustomSelect, CustomDate, CustomCheckbox, BufferSchemaView, Filters },
  data() {
    return {
      rowsData: [],
      bufferParamColumns,
      floors: [],
      filters: {
        resident_id: '',
        project_type_id: '',
        project_entrance_id: '',
        level_num: '',
        rowversion_from: '',
        rowversion_to: '',
      },
      selectedParamType: null,
      savedFilters: {},
      isFetching: false,
      selectedRows: [],
      allSelected: false,
      codeDebounceTimer: null,
    }
  },
  computed: {
    data() {
      return this.rowsData?.map((item, i) => ({ ...item, index: i + 1 }))
    },
    isAllFieldsSelected() {
      return this.filters.resident_id &&
        this.filters.project_type_id &&
        this.filters.project_entrance_id &&
        this.filters.level_num;
    },
    isDataReadyToShow() {
      if (this.hasAnyDateSelected) {
        return !this.isDateSelectionIncomplete;
      }
      if (this.isAllFieldsSelected) {
        return true;
      }
      return false;
    },
    hasAnyDateSelected() {
      return !!this.filters.rowversion_from || !!this.filters.rowversion_to;
    },
    isDateSelectionIncomplete() {
      const hasFromDate = !!this.filters.rowversion_from;
      const hasToDate = !!this.filters.rowversion_to;
      return (hasFromDate && !hasToDate) || (!hasFromDate && hasToDate);
    },
    selectedBufferParamsType() {
      if (!this.selectedRows.length || !this.rowsData.length) return null;

      const selectedData = this.rowsData.filter(item =>
        this.selectedRows.includes(item.floor_buffer_id)
      );

      return { selectedData, selectedParamType: this.selectedParamType };
    },
    selectedBufferParams() {
      if (!this.selectedRows.length || !this.rowsData.length) return null;

      const selectedData = this.rowsData.filter(item =>
        this.selectedRows.includes(item.floor_buffer_id)
      );

      const lines = [];
      const circles = [];

      selectedData.forEach(item => {
        let processedPoints = item.points;

        if (item.coord_type === 'LINESTRING') {
          lines.push({
            ...item,
            floor_param_id: item.floor_buffer_id,
            floor_param_type_color: item.color,
            points: processedPoints
          });
        } else if (item.coord_type === 'POINT') {
          circles.push({
            ...item,
            floor_param_id: item.floor_buffer_id,
            floor_param_type_color: item.color,
            points: processedPoints
          });
        }
      });

      return {
        lines,
        circles
      };
    },
  },
  methods: {
    async getFloors() {
      if (!this.filters.resident_id || !this.filters.project_entrance_id) {
        this.floors = []
        return
      }
      const res = await getFloors({
        resident_id: this.filters.resident_id,
        project_entrance_id: this.filters.project_entrance_id,
        project_type_id: this.filters.project_type_id,
      })
      this.floors = res || []
    },
    async getBufferData() {
      if (!this.isDataReadyToShow) {
        return;
      }
      this.isFetching = true
      const res = await getBufferParamsList(this.filters)
      this.isFetching = false
      if (!res) {
        return;
      }
      this.savedFilters = { ...this.filters }
      this.rowsData = res
      this.selectedRows = [];
      this.allSelected = false;
    },
    onFiltersChange(key, value) {
      if (['resident_id', 'project_type_id', 'project_entrance_id', 'level_num', 'rowversion_from', 'rowversion_to'].includes(key)) {
        this.filters[key] = value
      }

      if (key === 'resident_id') {
        this.filters = { [key]: value }
        this.rowsData = []
        this.selectedRows = []
        this.allSelected = false
      }
      if (key === 'project_type_id') {
        this.filters.project_entrance_id = ''
        this.filters.level_num = ''
        this.rowsData = []
        this.selectedRows = []
        this.allSelected = false
      }
      if (key === 'project_entrance_id') {
        this.filters.level_num = ''
        this.rowsData = []
        this.selectedRows = []
        this.allSelected = false
        this.getFloors()
      }
      if (key === 'level_num') {
        this.getBufferData()
      }
    },
    onAllCheckClick(value) {
      this.allSelected = value;
      if (value) {
        this.selectedRows = this.rowsData.map(item => item.floor_buffer_id);
      } else {
        this.selectedRows = [];
      }
    },
    onRowCheckClick(floor_buffer_id, value) {
      if (value) {
        this.selectedRows.push(floor_buffer_id);
      } else {
        this.selectedRows = this.selectedRows.filter(id => id !== floor_buffer_id);
      }
      this.allSelected = this.selectedRows.length === this.rowsData.length && this.rowsData.length > 0;
    },

    getPlaceholderMessage() {
      if (this.isDateSelectionIncomplete) {
        return 'Выберите дату начала и дату конца для отображения данных';
      }

      if (!this.isAllFieldsSelected) {
        return 'Сначала выберите ЖК, тип проекта, подъезд и этаж для отображения данных';
      }
    },
    onDateChange(key, value) {
      this.filters[key] = value;

      if (this.isDataReadyToShow) {
        this.getBufferData();
      }
    },
    applyNewRows(newRows) {
      this.rowsData = newRows;

      this.selectedRows = [];
      this.allSelected = false;
    },
    refreshData() {
      this.selectedRows = [];
      this.allSelected = false;

      if (this.isDataReadyToShow) {
        this.getBufferData();
      }
    },
  },
  setup() {
    const { showSucceedSnackbar } = useCustomSnackbar();
    return { showSucceedSnackbar };
  },
}
</script>

<style scoped lang="scss">
.wrapper {
  background-color: #FFFFFF;
  box-shadow: #4B5563;
  border-radius: 16px;
}

.wrapper_title {
  margin: 0;
  font-size: 32px;
  font-weight: 600;
  color: #4B5563;
}

:deep(.custom-select .q-field__control) {
  height: 46px !important;
}

:deep(.custom-select .q-field__append) {
  height: 46px !important;
  display: flex !important;
  align-items: center !important;
}

:deep(.q-field__control) {
  min-height: 46px !important;

  &::before {
    min-height: 46px !important;
  }

  &::after {
    min-height: 46px !important;
  }
}

:deep(.custom-date input) {
  min-height: 46px !important;
}

.content-layout {
  display: flex;
  gap: 20px;
}

.table-section {
  width: 60%;
  flex: 0 0 60%;
}

.placeholder-message {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 20px;
  width: 100%;
  height: 400px;
}

.placeholder-text {
  font-size: 16px;
  color: #4B5563;
  line-height: 1.5;
}

.header {
  display: flex;
  align-items: flex-end;
  gap: 15px;
  flex-wrap: wrap;

  &__item {
    width: 200px;
  }

  @media (max-width: 400px) {
    &__item {
      width: 100%;
    }
  }
}

.filters__item {
  width: 220px;

  &-label {
    font-size: 13px;
    line-height: 13px;
    margin-bottom: 8px;
  }
}

.q-table tbody td {
  font-size: 10px !important;
}
</style>
